/* eslint-disable react/jsx-no-bind */
// eslint-disable-next-line react/jsx-no-bind
import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FormikProvider, useFormik, Form } from 'formik';
import {
  Layout, Spin, Modal, Select, AutoComplete, Row, Col,
} from 'antd';
import {
  QuestionCircleOutlined,
} from '@ant-design/icons';
import isEqual from 'lodash.isequal';
import { useStore } from '../../../store';
import { useActions } from '../../providers/actions-provider/actions.context';
import { USER_MANAGEMENT_PATH } from '../../routing/paths.constants';
import FormControl from '../../lib/form-control';
import InputSelect from '../../lib/input-select';
import PageTitle from '../../lib/page-title';
import useGetUserDetails from './use-getUserDetails';
import './user-details.less';
import PrimaryButton from '../../lib/primary-button';
import SecondaryButton from '../../lib/secondary-button';
import { createValidator } from '../../../utils/create-validator';
import { EditUserFormDTO } from '../../../store/modules/user/dto/edit-user-form.dto';
import PermissionProvider from '../../providers/permissionProvider';
import checkPermission from '../../../utils/permissions/permissions';

const UserDetails = (): JSX.Element => {
  useGetUserDetails();
  const {
    pickedUser, roles, groups, facilities, currentUser, timeZones,
  } = useStore();
  const { userActions } = useActions();
  const { push } = useHistory();
  const { id } = useParams<{id:string}>();

  const { confirm } = Modal;
  const groupsList = Object.values(groups.data);
  const defaultGroup = groupsList.find((el) => el.code === pickedUser.data.group_code);
  const { Option } = Select;
  const [timeZonesList, setTimeZonesList] = useState<{ value: string, label: string }[]>([]);
  const hasNotWritePermission = !checkPermission('users', 'write');

  useEffect(() => {
    setTimeZonesList(timeZones.data.map((t) => ({ value: t, label: t })));
  }, [timeZones]);

  function deleteUser() {
    confirm({
      title: `Do you want to delete the ${pickedUser.data.name}?`,
      icon: <QuestionCircleOutlined style={{ color: '#F5222D' }} />,
      onOk: async () => {
        await userActions.deleteUser(+id);
        push(USER_MANAGEMENT_PATH);
      },
    });
  }

  const formik = useFormik({
    initialValues: {
      name: pickedUser.data.name,
      firstname: pickedUser.data.firstname,
      lastname: pickedUser.data.lastname,
      email: pickedUser.data.email,
      role_id: pickedUser.data.role_id,
      tenant: pickedUser.data.tenant,
      role_code: pickedUser.data.role_code,
      group_code: pickedUser.data.group_code,
      createdTime: pickedUser.data.createdTime,
      timeZone: pickedUser.data.timeZone,
    },
    onSubmit: async (values) => {
      const {
        role_id,
        tenant,
        group_code,
        name,
        firstname,
        lastname,
        email,
        createdTime,
        timeZone,
      } = values;
      await userActions.editPickedUser(+id, {
        role_id,
        tenant,
        group_code,
        name,
        firstname,
        lastname,
        email,
        createdTime,
        timeZone,
      });
      push(USER_MANAGEMENT_PATH);
    },
    validate: (values: EditUserFormDTO) => createValidator(EditUserFormDTO, values),
    enableReinitialize: true,
  });
  const { setFieldValue, values, initialValues } = formik;

  const onTimeZoneSearch = (searchText: string) => {
    const trimmedSearchText = searchText.trim();
    if (trimmedSearchText) {
      const opts = (timeZones.data || []).filter(
        (timeZone) => (
          timeZone.toLowerCase().includes(trimmedSearchText.toLowerCase())),
      );
      setTimeZonesList(opts.map((t) => ({ value: t, label: t })));
    } else {
      setTimeZonesList(timeZones.data.map((t) => ({ value: t, label: t })));
    }
  };

  function filterRolesByLoggedInUser() {
    let dataRoles = roles.data;
    const role = roles.data.find((r) => r.id === currentUser.data.role_id);
    switch (role?.code) {
      case 'tenant_root':
        dataRoles = roles.data.filter((r) => r.code !== 'root');
        break;
      case 'tenant_admin':
        dataRoles = roles.data.filter((r) => r.code !== 'root' && r.code !== 'tenant_root');
        break;
      case 'tenant_user':
        dataRoles = roles.data.filter((r) => r.code !== 'root' && r.code !== 'tenant_root' && r.code !== 'tenant_admin');
        break;
      default:
        break;
    }
    return dataRoles;
  }

  function switchRole(role?: string) {
    let returnedElement;
    switch (role) {
      case 'root':
        returnedElement = (
          <div className="formik-form__wrapper">
            <>
              <p>Role</p>
              <Select
                disabled={hasNotWritePermission}
                size="large"
                className="ant__select formik-form__dropdown"
                defaultValue={values.role_code || ''}
                onChange={(value: string | number) => {
                  const roleID = roles.data.find((el) => el.code === value);
                  setFieldValue('role_code', value);
                  setFieldValue('role_id', roleID?.id);
                }}
              >
                {filterRolesByLoggedInUser().map((el) => (
                  <Option key={el.id} value={el.code}>
                    {el.name}
                  </Option>
                ))}
              </Select>
            </>
          </div>
        );
        break;
      case 'tenant_root':
        returnedElement = (
          <div className="formik-form__wrapper">
            <>
              <p>Role</p>
              <Select
                disabled={hasNotWritePermission}
                size="large"
                className="ant__select formik-form__dropdown"
                defaultValue={values.role_code || ''}
                onChange={(value: string | number) => {
                  const roleID = roles.data.find((el) => el.code === value);
                  setFieldValue('role_code', value);
                  setFieldValue('role_id', roleID?.id);
                }}
              >
                {filterRolesByLoggedInUser().map((el) => (
                  <Option key={el.id} value={el.code}>
                    {el.name}
                  </Option>
                ))}
              </Select>
            </>
            {/* <InputSelect
              title="Tenant"
              defaultValue={pickedUser.data.tenant || ''}
              disabled
              fieldName="tenant"
              onChange={(value: string | number) => {
                setFieldValue('tenant', value);
              }}
              data={tenants.data}
            /> */}
          </div>
        );
        break;
      case 'tenant_admin':
      case 'tenant_user':
        returnedElement = (
          <div className="formik-form__wrapper">
            <>
              <p>Role</p>
              <Select
                size="large"
                className="ant__select formik-form__dropdown"
                defaultValue={values.role_code || ''}
                onChange={(value: string | number) => {
                  const roleID = roles.data.find((el) => el.code === value);
                  setFieldValue('role_code', value);
                  setFieldValue('role_id', roleID?.id);
                }}
              >
                {filterRolesByLoggedInUser().map((el) => (
                  <Option key={el.id} value={el.code}>
                    {el.name}
                  </Option>
                ))}
              </Select>
            </>
            {/* <InputSelect
              title="Tenant"
              defaultValue={pickedUser.data.tenant || ''}
              fieldName="tenant"
              disabled
              onChange={(value: string | number) => {
                setFieldValue('tenant', value);
              }}
              data={tenants.data}
            /> */}
            <InputSelect
              title="Facility"
              defaultValue={defaultGroup?.name || ''}
              fieldName="group_code"
              onChange={(value: string | number) => {
                // eslint-disable-next-line array-callback-return
                groupsList.find((el) => {
                  if (el.name === value) {
                    setFieldValue('group_code', el.code);
                  }
                });
              }}
              data={facilities.data}
              allowEmpty
            />
          </div>
        );
        break;
      default:
        break;
    }
    return returnedElement;
  }

  return (
    <Layout className="main-container user-details">
      <PermissionProvider pageId="users">
        <PageTitle title="User Details" back>
          {+id !== +currentUser.data.id && (
            <SecondaryButton
              disabled={hasNotWritePermission}
              className="left"
              onClick={deleteUser}
            >
              Delete User
            </SecondaryButton>
          )}
        </PageTitle>
        <FormikProvider value={formik}>
          { !pickedUser.loading ? (
            <Form className="formik-form" style={{ width: '100%' }}>
              <Row gutter={[10, 0]}>
                <Col style={{ minWidth: '300px' }}>
                  <FormControl wrapperClassName="formik-form__wrapper-input" inputClassName="formik-form__input" name="name" label="Username" type="text" disabled />
                  <FormControl wrapperClassName="formik-form__wrapper-input" inputClassName="formik-form__input" name="email" label="Email" type="text" disabled={hasNotWritePermission} />
                </Col>
                <Col style={{ minWidth: '300px' }}>
                  <FormControl wrapperClassName="formik-form__wrapper-input" inputClassName="formik-form__input" name="firstname" label="First Name" type="text" disabled={hasNotWritePermission} />
                  <FormControl wrapperClassName="formik-form__wrapper-input" inputClassName="formik-form__input" name="lastname" label="Last Name" type="text" disabled={hasNotWritePermission} />
                </Col>
              </Row>

              <div className="formik-form__separator" />
              <Row>
                <Col style={{ minWidth: '300px' }}>
                  <div className="formik-form__wrapper">
                    {switchRole(values.role_code)}
                  </div>
                  <>
                    <p className="ant-col ant-form-item-label" style={{ margin: '10px 0 5px' }}>Time Zone</p>
                    <AutoComplete
                      disabled={hasNotWritePermission}
                      options={timeZonesList}
                      value={values.timeZone || ''}
                      style={{ width: '100%' }}
                      onSearch={onTimeZoneSearch}
                      onChange={(value:string) => {
                        setFieldValue('timeZone', value);
                      }}
                      onFocus={() => {
                        setTimeZonesList(timeZones.data.map((t) => ({ value: t, label: t })));
                      }}
                    />
                  </>
                </Col>
              </Row>

              <PrimaryButton
                type="submit"
                className="formik-form__submit"
                disabled={isEqual(values, initialValues) || hasNotWritePermission}
              >
                Save
              </PrimaryButton>
            </Form>
          ) : <Spin size="large" />}
        </FormikProvider>
      </PermissionProvider>
    </Layout>
  );
};

UserDetails.displayName = 'UserDetails';

export default UserDetails;
